import { useState } from 'react';
import { api, useApi, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Loading, Sheet, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';
import { describeRule } from './Schedule.tsx';
import type { Asset } from './Library.tsx';

interface AudioProgram { id: string; name: string; ceilingBp: number; duckToBp: number; fadeMs: number; dayparts: { daypartId: string; rules: Parameters<typeof describeRule>[0][]; trackAssetIds: string[]; volumeBp: number }[]; enabled: boolean }

export function Music() {
  const org = useOrg();
  const toast = useToast();
  const programs = useApi<{ audioPrograms: AudioProgram[] }>(orgPath(org, '/audio-programs'), [org.id]);
  const tracks = useApi<{ assets: Asset[] }>(orgPath(org, '/assets?kind=audio'), [org.id]);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ name: 'Store music', start: '09:00', end: '21:00', volume: 70, ceiling: 80, duck: 20, tracks: [] as string[] });
  async function save() {
    try {
      await api('POST', orgPath(org, '/audio-programs'), { name: f.name, ceilingBp: f.ceiling * 100, duckToBp: f.duck * 100, dayparts: [{ rules: [{ startTime: f.start, endTime: f.end }], trackAssetIds: f.tracks, volumeBp: f.volume * 100 }] });
      toast('Music program saved. Publish to send it to screens.');
      setCreating(false);
      void programs.reload();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  return (
    <>
      <div className="page-head">
        <div><h1>Music</h1><p>Background audio runs independently of the visual playlist. In a shared space, only one screen per acoustic zone plays music.</p></div>
        {can(org, 'schedules.write') && <button className="btn btn-primary" onClick={() => setCreating(true)}><Icon name="plus" /> Music program</button>}
      </div>
      <div className="alert info" style={{ marginBottom: 16 }}>
        Upload only music you have public-performance rights for and record the rights owner on each track. A personal streaming subscription does not license business playback. Licensed providers: <strong>Connect provider</strong> (not configured).
      </div>
      {programs.error && <ErrorState error={programs.error} />}
      {!programs.data ? <Loading /> : !programs.data.audioPrograms.length ? <div className="card"><Empty title="No music programs" /></div> : (
        <div className="grid grid-cards">
          {programs.data.audioPrograms.map((p) => (
            <div key={p.id} className="card card-pad stack">
              <h3>{p.name}</h3>
              {p.dayparts.map((d) => <span key={d.daypartId} className="subtle">{d.rules.map(describeRule).join('; ')} · {d.trackAssetIds.length} tracks · volume {d.volumeBp / 100}%</span>)}
              <span className="subtle">Ceiling {p.ceilingBp / 100}% · video ads duck music to {p.duckToBp / 100}% · {p.fadeMs} ms fades</span>
            </div>
          ))}
        </div>
      )}
      {creating && (
        <Sheet title="New music program" onClose={() => setCreating(false)} footer={<button className="btn btn-primary" disabled={!f.tracks.length} onClick={() => void save()}>Save</button>}>
          <div className="stack" style={{ gap: 14 }}>
            <div className="field"><label htmlFor="m-name">Name</label><input id="m-name" className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div className="row">
              <div className="field"><label htmlFor="m-s">Daily start (local)</label><input id="m-s" type="time" className="input" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></div>
              <div className="field"><label htmlFor="m-e">End</label><input id="m-e" type="time" className="input" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} /></div>
            </div>
            <div className="row">
              {(['volume', 'ceiling', 'duck'] as const).map((k) => (
                <div key={k} className="field"><label htmlFor={`m-${k}`}>{k === 'duck' ? 'Duck to (%)' : `${k[0]!.toUpperCase()}${k.slice(1)} (%)`}</label><input id={`m-${k}`} type="number" min={0} max={100} className="input" value={f[k]} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} /></div>
              ))}
            </div>
            <fieldset className="stack" style={{ border: 0, padding: 0 }}>
              <legend className="label">Tracks</legend>
              {tracks.data?.assets.filter((a) => a.status === 'ready').map((a) => (
                <label key={a.id} className="row"><input type="checkbox" checked={f.tracks.includes(a.id)} onChange={(e) => setF({ ...f, tracks: e.target.checked ? [...f.tracks, a.id] : f.tracks.filter((x) => x !== a.id) })} />{a.name}{!a.rightsOwner && <span className="badge warn plain">no rights owner</span>}</label>
              ))}
            </fieldset>
          </div>
        </Sheet>
      )}
    </>
  );
}
